import React from 'react'
import { connect } from 'react-redux'
import { Helmet } from 'react-helmet'
import styled from 'styled-components'
import { darken } from 'polished'
import { Formik, Form } from 'formik'
import isEmpty from 'lodash/isEmpty'
import { ClipLoader } from 'react-spinners'

// Actions
import {
  formActions,
  businessActions,
  userActions,
  dealActions,
  businessOfficersActions,
  topBusinessDebtorsActions,
  additionalBusinessDataActions,
  notificationsActions,
  bankActions,
  businessBankAccountActions,
} from '../_actions'

// Services
import { emailService } from '../_services'

// Constants
import { formConfig } from '../_configs'
import ROUTES from '../_constants/routes'
import colors, { tenantColors } from '../_constants/colors'

// Components
import { Breadcrumbs } from '../_components/Breadcrumbs'
import { S_FormTracker } from '../_components/FormTracker/FormTracker.styles'
import { Button } from '../_components/Button'
import ToolTip from '../_components/Tooltip/Tooltip'
import Field from '../_components/FieldNew'
import { BusinessOfficers } from '../_components/BusinessOfficers'
import { FormSummary } from '../_components/FormSummary/FormSummary'
import { LargestCustomers } from '../_components/LargestCustomers'
import { bankConstants } from '../_constants'
import Slider from '../_components/Slider'
import { Invoices } from '../_components/Invoices'
import { OriginateFormHeader } from '../_components/OriginateFormHeader'
import { convertDatesFromDateTime, currentDateTimeInUTC, UTCDateToSystemString } from '../_helpers'
import { businessDateFields } from '../_helpers/business'
import { officersDateFields } from '../_helpers/business_officers'
import { FormTracker } from '../_components/FormTracker'
import { H2 } from '../_components/Typography/Components/Headings/H2'
import { S_QuestionRoundedIcon } from '../_components/ListSearchableView'
import { LoaderButton } from '../_components/LoaderButton'
import { COMPANY_NAME } from 'xen/constants'

// Styled Componenets
const ConfirmForm = styled.div`
  .main-content {
    position: relative;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    max-width: 830px;
    padding: 50px 15px 70px;

    .loading-spinner {
      margin: 0 auto;
    }

    .confirm-form {
      display: flex;
      flex-direction: column;

      ${S_FormTracker} {
        margin: 0 0 30px;
        padding: 20px 0;
      }

      .form-controls {
        display: flex;
        justify-content: center;
        align-items: center;
        flex-wrap: wrap;
        margin: -10px;

        .back-btn,
        button {
          margin: 10px;
        }

        .back-btn {
          opacity: 0.5;
          font-size: 16px;
          padding: 10px;
          text-decoration: underline;
          cursor: pointer;
          transition: all 0.2s ease-in-out;
          margin-right: 30px;

          &:hover {
            opacity: 0.75;
          }
        }

        .review-btn {
          &:hover {
            background-color: ${darken(0.1, colors.MATERIAL_GREEN)};
          }
        }
      }
    }
  }

  .sections {
    max-width: 100%;
    margin-left: auto;
    margin-right: auto;
  }
`

const Section = styled.div`
  outline: none;

  .section-header {
    display: flex;
    align-items: baseline;
    flex-direction: column;
    margin-bottom: 15px;

    ${S_QuestionRoundedIcon} {
      margin-left: 3px;
    }
  }

  .section-header-element {
    display: flex;
    align-items: center;
  }

  .section-fields {
    display: flex;
    flex-direction: column;
    margin-bottom: 50px;

    .upload-documents-explainer {
      margin-top: 20px;
      display: grid;
      grid-gap: 10px;

      .explainer {
        .sub-explainer {
          opacity: 0.5;
        }

        ${S_QuestionRoundedIcon} {
          margin-left: 3px;
        }
      }
    }

    .form-group {
      display: flex;
      flex-direction: column;
    }
  }
`

const CustomObservation = styled.h4`
  margin-top: 10px;
  margin-bottom: 20px;
  font-weight: normal;
  font-style: italic;
`

class ConfirmFormPage extends React.Component {
  constructor(props) {
    super(props)

    this.slider = React.createRef()
    this.mainDiv = React.createRef()
    this.state = {
      inReview: false,
      isSubmitting: false,
      reviewBtnPressed: false,
      waitingForBusinessUpdate: false,
    }
    this.fieldStates = {}
    this.currentSection = 0
  }

  FIELD_SECTION_LOOKUP = {}

  componentDidMount() {
    const { activeSection } = this.props

    if (this.slider.current) {
      this.slider.current.slickGoTo(activeSection - 1)
    }
    this.updateCompletedSections()
  }

  updateCompletedSections = (parentFieldTable, parentFieldName, condition) => {
    const { tenant, completed, completedSections, activeSection } = this.props
    const sections = formConfig[tenant].sections.map((section) => {
      return {
        ...section,
        fields: section.fields
          .reduce((arr, field) => {
            //we're not going to validate multiselect fields, until there's a better implementation of them.
            if (field.type != 'multiselect') {
              arr.push(field)
              if (field.following_question) {
                arr.push(field.following_question)
              }
            }
            return arr
          }, [])
          .filter((field) => field.required),
      }
    })
    let newCompleted = sections.map((section, i) => {
      let sectionComplete = section.fields.reduce((prev, current) => {
        let currentIsValid =
          this.fieldStates[current.table] &&
          (this.fieldStates[current.table][current.name] || this.fieldStates[current.table][current.name] == 0) &&
          this.fieldStates[current.table][current.name].value != null &&
          this.fieldStates[current.table][current.name].value != undefined &&
          this.fieldStates[current.table][current.name].value !== '' &&
          !this.fieldStates[current.table][current.name].error

        if (
          this.fieldStates[current.table] &&
          current.type === 'radio' &&
          this.fieldStates[current.table][current.name].value === false
        ) {
          currentIsValid = true
        }

        if (current.condition != undefined && !parentFieldTable && !parentFieldName) {
          currentIsValid = true
        }
        if (
          this.fieldStates[current.table] &&
          this.fieldStates[current.table][current.name] &&
          !this.fieldStates[current.table][current.name].mounted
        ) {
          currentIsValid = true
        }
        return !!(prev && currentIsValid)
      }, true)
      //Note: activeSection is 1 based counting, not 0 based
      if (sectionComplete && this.state.reviewBtnPressed) {
        return true
      }
      return sectionComplete && i < activeSection
    })
    let hasChanged = newCompleted.map((b, i) => b != completed[i])

    if (hasChanged.some((b) => b)) {
      completedSections(newCompleted)
    }
  }
  fieldStateUpdate = (
    table,
    fieldName,
    error,
    touched,
    value,
    index,
    parentFieldTable,
    parentFieldName,
    condition,
    mounted
  ) => {
    if (!this.fieldStates[table]) {
      this.fieldStates[table] = {}
    }
    if (!index) {
      this.fieldStates[table][fieldName] = { table, fieldName, error, touched, value, mounted }
      this.updateCompletedSections(parentFieldTable, parentFieldName, condition)
    }
  }
  handleBlur = (setFieldTouched, name, attr, val, table, invalid = false, businessOfficerOrdinal = null) => {
    const {
      business,
      updateBusiness,
      user,
      updateUser,
      updateBusinessOfficer,
      deal,
      updateDeal,
      topBusinessDebtors,
      updateDebtor,
      updateBusinessDebtor,
      createBusinessDebtor,
      updateAdditionalBusinessData,
      updateBank,
      updateBusinessBankAccount,
      featureFlags,
    } = this.props

    setFieldTouched(name, true)
    if (invalid && val) {
      return
    }
    if (table === `business`) {
      updateBusiness(attr, val, business.id)
    } else if (table === `user`) {
      updateUser(attr, val, user.id)
    } else if (table === `business_officers`) {
      updateBusinessOfficer(attr, val, business.id, businessOfficerOrdinal)
    } else if (table === `deal`) {
      updateDeal(attr, val, deal.id)
    } else if (table === `bank`) {
      updateBank(val, user.id, deal.id)
    } else if (table === `top_business_debtors`) {
      if (!val) {
        return null
      }
      if (attr === `debtor_name` || attr === `debtor_city` || attr === 'debtor_state') {
        // if debtor hasn't been created,create new businessDebtor and debtor
        if (!topBusinessDebtors.data[businessOfficerOrdinal].debtor_id) {
          createBusinessDebtor(val, business.id, businessOfficerOrdinal)
          // if debtor has already been created, update debtor name and get debtor_id of new or existing debtor entry
        } else {
          updateDebtor(
            attr,
            val,
            topBusinessDebtors.data[businessOfficerOrdinal].debtor_id,
            topBusinessDebtors.data[businessOfficerOrdinal].id
          )
        }
      } else {
        updateBusinessDebtor(attr, val, topBusinessDebtors.data[businessOfficerOrdinal].id)
      }
    } else if (table === `additional_business_data`) {
      updateAdditionalBusinessData(attr, val, business.id)
    } else if (table === `business_bank_account`) {
      updateBusinessBankAccount(attr, val, business.id)
    }
  }
  handleCustomerRemove = (id, ordinal, businessId, setValues, values) => {
    const handleRemoveCallback = () => {
      values.top_business_debtors = values.top_business_debtors.filter((item, index) => index !== ordinal - 1)
      setValues(values)
    }
    this.props.removeTopBusinessDebtor(id, ordinal, businessId, handleRemoveCallback)
  }
  handleChange = (setFieldValue, attr, val) => {
    setFieldValue(attr, val)
  }
  handleSectionChange = (sectionIndex, validateForm, noScroll = false, validationEvent = false) => {
    const { activeSection, goToSection, tenant } = this.props

    const section = formConfig[tenant].sections[sectionIndex - 1]
    validateForm().then((errors) => {
      if (Object.keys(errors).length === 0) {
        goToSection(activeSection, sectionIndex, true)
      } else {
        goToSection(activeSection, sectionIndex, this.sectionComplete(errors, section))
      }

      if (!noScroll) {
        this.scrollToTop()
      }

      this.slider.current.slickGoTo(sectionIndex - 1, validationEvent ? true : false)
      this.updateCompletedSections()
      if (validationEvent) {
        this.focusOnError()
      }
    })
  }
  handleCompleteForm = async () => {
    const {
      business,
      updateBusiness,
      updateAdditionalBusinessData,
      user,
      history,
      updateDeal,
      setFlowStep,
      deal,
      showNotification,
      authentication,
    } = this.props

    const updateBusinessToSubmitted = async () => {
      updateBusiness(`completed_profile`, true, business.id)
      updateDeal(`deal_status`, `submitted`, deal.id)
      updateAdditionalBusinessData(
        'submitted_at',
        UTCDateToSystemString(currentDateTimeInUTC(), false, false, true),
        business.id
      )
      setFlowStep(
        user.id,
        authentication.via_prospect_link
          ? 'prospect_link_application_submitted'
          : user.flow_step === 'invoices_added_by_new_user'
            ? 'invoices_submitted_by_new_user'
            : user.flow_step === 'chosen_accounting_option'
              ? 'client_application_submitted'
              : 'skip_invoices_application_submitted'
      )
    }

    this.setState({
      isSubmitting: true,
    })

    emailService
      .deliverAdminEmail(`user_submission`, {}, user.id)
      .then(async () => {
        await updateBusinessToSubmitted().then(() => {
          this.setState({
            waitingForBusinessUpdate: true,
          })
        })
      })
      .catch((_) => {
        showNotification({
          type: `error`,
          message: `Unable to submit form.`,
          id: `submit-form-fail`,
        })
        this.setState({
          isSubmitting: false,
        })
      })
  }

  sectionComplete = (errors, section) => {
    // Errors are nested, this function unnests them
    function flattenErrors(errorObject, tableName) {
      let out = []

      Object.entries(errorObject).forEach(([key, val]) => {
        // If the error is an array, we need to flatten it and add it to out
        if (!val) return
        if (val.constructor === Array) out.push(...flattenErrors(val, key))
        // Return table.fieldName
        else out.push(...Object.keys(val).map((fieldName) => `${tableName || key}.${fieldName}`))
      })

      return out
    }

    // Check to see if an errored field exists in errors and see if any have this section's title
    return flattenErrors(errors).every((error) => this.FIELD_SECTION_LOOKUP[error] !== section.title)
  }
  goToError = (errors, validateForm, setFieldTouched) => {
    const { tenant } = this.props
    // Helper function - set each as touched
    const setFieldListTouched = (fieldNames, prefix = '') => {
      fieldNames.forEach((fieldName) => {
        setFieldTouched(prefix + fieldName, true)
      })
    }
    // Set every field as touched, can iterate over nested form groups
    Object.keys(errors).forEach((fieldGroupName) => {
      const fieldErrors = errors[fieldGroupName]
      if (fieldErrors) {
        if (Array.isArray(fieldErrors)) {
          fieldErrors.forEach((fieldErrorsSubGroup, i) => {
            if (!fieldErrorsSubGroup) return
            const fieldNames = Object.keys(fieldErrorsSubGroup)
            setFieldListTouched(fieldNames, ``) /* ${fieldGroupName}[${i}]. */
          })
        } else if (typeof fieldErrors === 'string') {
          // Still finding optimal method , but sometimes fieldGroupName is a singular form field
          // Set this as touched directly
          setFieldTouched(fieldGroupName, true)
        } else {
          // Map
          const fieldNames = Object.keys(fieldErrors)
          setFieldListTouched(fieldNames)
        }
      }
    })

    const sections = formConfig[tenant].sections.filter((section) => section.type !== 'new_prospect_form_lender')

    sections.some((section, i) => {
      const sectionNo = i + 1

      if (!this.sectionComplete(errors, section)) {
        this.handleSectionChange(sectionNo, validateForm, true, true)
        this.currentSection = sectionNo - 1
        return true
      }

      return false
    })
    validateForm()
  }
  populateFieldSectionLookup = (section) => {
    const populate = (fields) => {
      fields.forEach((field) => {
        // If there is a group of fields, we need to go through them
        if (field.constructor === Array) {
          populate(field)
        } else {
          this.FIELD_SECTION_LOOKUP[`${field.table}.${field.name}`] = section.title
          if (field.following_question) {
            this.FIELD_SECTION_LOOKUP[`${field.following_question.table}.${field.following_question.name}`] =
              section.title
          }
        }
      })
    }

    populate(section.fields || section)
  }
  handleEditForm = () => {
    const { activeSection } = 1
    this.setState({
      inReview: false,
    })
    this.slider.current.slickGoTo(activeSection - 1)
  }
  handleEditSection = (section) => {
    const { goToSection } = this.props
    this.setState({
      inReview: false,
    })
    goToSection(null, section + 1, false)
    this.slider.current.slickGoTo(section)
    this.currentSection = section
    this.scrollToTop()
  }
  objectNullToString = (obj) => {
    var out = {}
    Object.entries(obj).forEach(([key, val]) => {
      if (val === null || val === undefined) out[key] = ''
      else if (val.constructor === Array) out[key] = Object.values(this.objectNullToString(val))
      else if (typeof val === 'object') out[key] = this.objectNullToString(val)
      else out[key] = val
    })

    return out
  }
  scrollToTop = () => {
    if (this.mainDiv.current) {
      this.mainDiv.current.scrollIntoView({ behavior: 'smooth', inline: 'end' })
    }
  }

  focusOnError = () => {
    setTimeout(() => {
      const errors = document.getElementsByClassName('invalid-input')
      if (!errors) {
        return
      }
      const inputHeight = 90
      const elementTop = errors[0]?.getBoundingClientRect().top
      if (!elementTop) {
        return
      }
      const errorScroll = elementTop - inputHeight
      const scrollOffset = window.pageYOffset

      window.scrollTo({
        top: scrollOffset + errorScroll,
        left: 0,
        behavior: 'smooth',
      })
    }, 100)
  }

  renderUploadDocumentsExplainer(section, formConfig) {
    const explainer = section.explainer || formConfig.defaultOptions.uploadDocuments.explainer
    const subExplainer = section.subExplainer || formConfig.defaultOptions.uploadDocuments.subExplainer
    const toolTip = section.toolTip || formConfig.defaultOptions.uploadDocuments.toolTip
    return (
      <div className={`upload-documents-explainer`} key={`upload- v-explainer`}>
        <p className={`explainer`}>{explainer}</p>
        <p className={`explainer`}>
          <span className={`sub-explainer`}>{subExplainer}</span>
          <ToolTip content={toolTip}>
            <S_QuestionRoundedIcon size={'sm'} />
          </ToolTip>
        </p>
      </div>
    )
  }

  render() {
    const {
      tenant,
      activeSection,
      completed,
      business,
      user,
      deal,
      businessOfficers,
      topBusinessDebtors,
      additionalBusinessData,
      bank,
      activeCustomers,
      history,
      state,
      businessBankAccount,
      showNotification,
    } = this.props
    const tenantFormConfig = formConfig[tenant]
    tenantFormConfig.sections = tenantFormConfig.sections.filter(
      (section) => section.type !== 'new_prospect_form_lender'
    )
    const formDataLoaded =
      business &&
      user &&
      businessOfficers.data &&
      deal &&
      topBusinessDebtors.data &&
      additionalBusinessData &&
      bank[bankConstants.ATTR_NAME]

    if (!this.initialValues && formDataLoaded) {
      let startingValues = {
        business: convertDatesFromDateTime(business, businessDateFields),
        user: { ...user },
        business_officers: businessOfficers.data.map((officer) =>
          convertDatesFromDateTime(officer, officersDateFields)
        ),
        deal: { ...deal },
        bank: bank,
        top_business_debtors: [...topBusinessDebtors.data],
        additional_business_data: { ...additionalBusinessData },
        business_bank_account: businessBankAccount,
      }
      this.currentSection = activeSection - 1
      this.initialValues = this.objectNullToString(startingValues)
      this.fieldStates = formConfig[tenant].sections
        .map((section) => {
          return {
            ...section,
            fields: section.fields
              .reduce((arr, field) => {
                arr.push(field)
                if (field.following_question) {
                  arr.push(field.following_question)
                }
                return arr
              }, [])
              .filter((field) => field.required),
          }
        })
        .reduce((fieldState, section) => {
          section.fields.forEach((field) => {
            if (!fieldState[field.table]) {
              fieldState[field.table] = {}
            }
            let val = startingValues[field.table][field.name]
            if (
              field.table == 'business_officers' &&
              startingValues[field.table].data &&
              startingValues[field.table].data.length > 0
            ) {
              //sometimes business officers are in a property called data, sometimes business_officers proprty of starting values is an array itself
              val = startingValues[field.table].data[0][field.name]
            }
            if (
              field.table == 'business_officers' &&
              startingValues[field.table] &&
              startingValues[field.table].length > 0
            ) {
              //sometimes business officers are in a property called data, sometimes business_officers proprty of starting values is an array itself
              val = startingValues[field.table][0][field.name]
            }
            fieldState[field.table][field.name] = {
              table: field.table,
              fieldName: field.name,
              error: '',
              touched: false,
              value: val,
            }
          })
          return fieldState
        }, {})
    }

    const user_submitted =
      user.flow_step === 'skip_invoices_application_submitted' ||
      user.flow_step === 'client_application_submitted' ||
      user.flow_step === 'prospect_link_application_submitted'

    if (business.completed_profile && user_submitted && this.state.waitingForBusinessUpdate) {
      this.setState({
        isSubmitting: false,
      })
      showNotification({
        type: `success`,
        message: `Form submmitted successfully.`,
        id: `submit-form-success`,
      })
      history.push(ROUTES.submitted)
    }

    return (
      <ConfirmForm>
        <Helmet>
          <title>Confirm Form • {COMPANY_NAME}</title>
        </Helmet>
        <Breadcrumbs activeItem={`Business Details`} />
        <div className={`main-content`} ref={this.mainDiv}>
          <OriginateFormHeader
            className={`formm-header`}
            subtitle={business.business_name || user.email_address}
            title="Business Details"
          />
          {activeCustomers.length > 0 && <Invoices />}
          {!formDataLoaded ? (
            <div className={`loading-spinner`}>
              <ClipLoader color={tenantColors[tenant].secondary} loading={true} size={100} />
            </div>
          ) : (
            <Formik
              initialValues={this.initialValues}
              onSubmit={(values, action) => {
                this.setState({
                  inReview: true,
                })
                this.slider.current.slickGoTo(tenantFormConfig.sections.length)
              }}
            >
              {({ setFieldTouched, setFieldValue, values, errors, touched, isValid, validateForm, setValues }) => (
                <Form className={`confirm-form`}>
                  {!this.state.inReview && (
                    <FormTracker
                      currentSection={this.currentSection}
                      onSectionChange={(newSection) => {
                        this.currentSection = newSection
                        this.handleSectionChange(newSection + 1, validateForm)
                      }}
                      sections={tenantFormConfig.sections}
                    />
                  )}
                  <Slider className={`sections`} intialSlide={activeSection - 1} ref={this.slider}>
                    {/* Loops over the sections of the form */}
                    {tenantFormConfig.sections.map((section, i) => {
                      // Check to see if this section has been iterated over before to improve performance
                      if (!Object.values(this.FIELD_SECTION_LOOKUP).includes(section.title)) {
                        this.populateFieldSectionLookup(section)
                      }

                      return (
                        <Section active={activeSection === i + 1} className={`section`} key={i}>
                          <div className={`section-header`}>
                            <div className={`section-header-element`}>
                              <H2 mr={'5px'}>{section.section_title || section.title}</H2>
                              {section.tooltip && (
                                <ToolTip content={section.tooltip}>
                                  <S_QuestionRoundedIcon />
                                </ToolTip>
                              )}
                            </div>
                            {section.custom_observation && (
                              <CustomObservation>{section.custom_observation}</CustomObservation>
                            )}
                          </div>
                          <div className={`section-fields`}>
                            {/* Loops over the fields within a particular section */}
                            {section.type === `business_officers` ? (
                              <BusinessOfficers
                                data={businessOfficers.data}
                                errors={errors}
                                fieldStateUpdate={this.fieldStateUpdate}
                                fields={section.fields}
                                fill_btn={section.fill_btn}
                                handleBlur={(name, attr, val, businessOfficerOrdinal, isDocument, invalid) => {
                                  this.handleBlur(
                                    setFieldTouched,
                                    name,
                                    attr,
                                    val,
                                    isDocument ? `deal` : `business_officers`,
                                    invalid,
                                    businessOfficerOrdinal
                                  )
                                }}
                                handleChange={(attr, val) => this.handleChange(setFieldValue, attr, val)}
                                label={section.label}
                                overrideShowError={this.state.reviewBtnPressed}
                                setValues={setValues}
                                touched={touched}
                                user={user}
                                values={values}
                              />
                            ) : section.type === `largest_customers` ? (
                              <LargestCustomers
                                data={topBusinessDebtors.data}
                                errors={errors}
                                fieldStateUpdate={this.fieldStateUpdate}
                                fields={section.fields}
                                handleBlur={(name, attr, val, businessOfficerOrdinal) =>
                                  this.handleBlur(
                                    setFieldTouched,
                                    name,
                                    attr,
                                    val,
                                    `top_business_debtors`,
                                    false,
                                    businessOfficerOrdinal
                                  )
                                }
                                handleChange={(attr, val) => this.handleChange(setFieldValue, attr, val)}
                                handleCustomerRemove={(id, ordinal, businessId) =>
                                  this.handleCustomerRemove(id, ordinal, businessId, setValues, values)
                                }
                                label={section.label}
                                maxLength={section.maxLength}
                                overrideShowError={this.state.reviewBtnPressed}
                                required={section.required}
                                touched={touched}
                                values={values}
                              />
                            ) : (
                              section.fields.map((field, i) =>
                                Array.isArray(field) ? (
                                  [
                                    section.type === `upload_documents` &&
                                      this.renderUploadDocumentsExplainer(section, formConfig),
                                    <div className={`form-group`} key={i}>
                                      {field.map((nestedField, ii) => (
                                        <Field
                                          errors={errors}
                                          field={nestedField}
                                          fieldStateUpdate={this.fieldStateUpdate}
                                          handleBlur={(name, attr, val, fieldTable = null, invalid = false) => {
                                            this.handleBlur(
                                              setFieldTouched,
                                              name,
                                              attr,
                                              val,
                                              fieldTable || nestedField.table,
                                              invalid
                                            )
                                          }}
                                          handleChange={(attr, val) => this.handleChange(setFieldValue, attr, val)}
                                          key={ii}
                                          overrideShowError={this.state.reviewBtnPressed}
                                          state={state}
                                          touched={touched}
                                          values={values}
                                        />
                                      ))}
                                    </div>,
                                  ]
                                ) : (
                                  <Field
                                    errors={errors}
                                    field={field}
                                    fieldStateUpdate={this.fieldStateUpdate}
                                    handleBlur={(name, attr, val, fieldTable = null, invalid = false) =>
                                      this.handleBlur(
                                        setFieldTouched,
                                        name,
                                        attr,
                                        val,
                                        fieldTable || field.table,
                                        invalid
                                      )
                                    }
                                    handleChange={(attr, val) => this.handleChange(setFieldValue, attr, val)}
                                    key={i}
                                    overrideShowError={this.state.reviewBtnPressed}
                                    state={state}
                                    touched={touched}
                                    values={values}
                                  />
                                )
                              )
                            )}
                          </div>
                        </Section>
                      )
                    })}
                    <FormSummary
                      errors={errors}
                      handleBlur={(name, attr, val) => this.handleBlur(setFieldTouched, name, attr, val, ``)}
                      handleChange={(attr, val) => this.handleChange(setFieldValue, attr, val)}
                      handleEditSection={this.handleEditSection}
                      touched={touched}
                      values={values}
                    />
                  </Slider>
                  <div className={`form-controls`}>
                    {!this.state.inReview
                      ? [
                          activeSection > 1 && (
                            <span
                              className={`back-btn`}
                              key={0}
                              onClick={() => {
                                this.handleSectionChange(activeSection - 1, validateForm),
                                  (this.currentSection = this.currentSection - 1)
                              }}
                            >
                              Back
                            </span>
                          ),
                          activeSection < tenantFormConfig.sections.length && (
                            <Button
                              buttonType={`button`}
                              key={1}
                              onClick={() => {
                                this.handleSectionChange(activeSection + 1, validateForm),
                                  (this.currentSection = this.currentSection + 1)
                              }}
                              text={`Next`}
                              type={`button`}
                            />
                          ),
                          (activeSection === tenantFormConfig.sections.length ||
                            (completed.every((el) => el === true) && isEmpty(errors))) && (
                            <Button
                              buttonType={`button`}
                              className={`review-btn`}
                              key={2}
                              onClick={() => {
                                this.setState({ reviewBtnPressed: true })
                                if (!isEmpty(errors)) {
                                  this.goToError(errors, validateForm, setFieldTouched)
                                }
                                if (isEmpty(errors)) {
                                  this.scrollToTop()
                                }
                              }}
                              text={`Review`}
                              type={`submit`}
                            />
                          ),
                        ]
                      : [
                          <Button
                            buttonType={`button`}
                            key={1}
                            onClick={() => {
                              this.handleEditForm(),
                                this.handleSectionChange(1, validateForm),
                                (this.currentSection = 0)
                            }}
                            text={`Edit`}
                            type={`button`}
                          />,
                          <LoaderButton
                            buttonType={`button`}
                            className={`submit-btn`}
                            disabled={tenantFormConfig.agree_btn && !values.agree}
                            isLoading={this.state.isSubmitting}
                            key={0}
                            onClick={() => this.handleCompleteForm(validateForm, values)}
                            text={`Submit`}
                            type={`button`}
                          />,
                        ]}
                  </div>
                </Form>
              )}
            </Formik>
          )}
        </div>
      </ConfirmForm>
    )
  }
}

function mapStateToProps(state) {
  const {
    env,
    form,
    user,
    business,
    deal,
    businessOfficers,
    topBusinessDebtors,
    additionalBusinessData,
    authentication,
    bank,
    accounting,
    businessBankAccount,
    featureFlags,
  } = state
  return {
    tenant: env.tenant,
    activeSection: form.activeSection,
    completed: form.completed,
    user,
    business,
    deal: deal.deal,
    businessOfficers,
    topBusinessDebtors,
    additionalBusinessData,
    authentication,
    bank: bank,
    activeCustomers: accounting.activeCustomers,
    businessBankAccount,
    featureFlags,
    state,
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    updateBusiness: (attr, val, businessId) => {
      dispatch(businessActions.updateBusiness(attr, val, businessId))
    },
    updateUser: (attr, val, userId) => {
      dispatch(userActions.updateUser(attr, val, userId))
    },
    updateBusinessOfficer: (attr, val, businessId, businessOfficerOrdinal) => {
      dispatch(businessOfficersActions.updateBusinessOfficer(attr, val, businessId, businessOfficerOrdinal))
    },
    goToSection: (prevSection, nextSection, completed) => {
      dispatch(formActions.goToSection(prevSection, nextSection, completed))
    },
    completedSections: (sectionsComplete) => {
      dispatch(formActions.completedSections(sectionsComplete))
    },
    updateDeal: (attr, val, dealId) => {
      dispatch(dealActions.updateDeal(attr, val, dealId))
    },
    updateDebtor: (attr, val, debtorId) => {
      dispatch(topBusinessDebtorsActions.updateDebtor(attr, val, debtorId))
    },
    updateBusinessDebtor: (attr, val, businessDebtorId) => {
      dispatch(topBusinessDebtorsActions.updateBusinessDebtor(attr, val, businessDebtorId))
    },
    createBusinessDebtor: (debtorName, businessId, index) => {
      dispatch(topBusinessDebtorsActions.createBusinessDebtor(debtorName, businessId, index))
    },
    updateAdditionalBusinessData: (attr, val, businessId) => {
      dispatch(additionalBusinessDataActions.updateAdditionalBusinessData(attr, val, businessId))
    },
    showNotification: (notification) => {
      dispatch(notificationsActions.showNotification(notification))
    },
    setFlowStep: (userId, value) => {
      dispatch(userActions.updateUser('flow_step', value, userId))
    },
    updateBank: (value, userId, dealId) => {
      dispatch(bankActions.connectBank(value, userId, dealId))
    },
    removeTopBusinessDebtor: (id, ordinal, businessId, handleRemoveCallback) => {
      if (!id) {
        dispatch(topBusinessDebtorsActions.removeTopBusinessDebtor(ordinal, handleRemoveCallback))
      } else {
        dispatch(topBusinessDebtorsActions.deleteTopBusinessDebtor(businessId, id, handleRemoveCallback))
      }
    },
    updateBusinessBankAccount: (attr, val, businessId) => {
      dispatch(businessBankAccountActions.updateBusinessBankAccount(attr, val, businessId))
    },
  }
}

const connectedConfirmFormPage = connect(mapStateToProps, mapDispatchToProps)(ConfirmFormPage)
export { connectedConfirmFormPage as ConfirmFormPage }
